import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Projects.css';

interface Project {
  id: number;
  category: string;
  title: string;
  description: string;
  image: string;
  github: string;
  external: string;
}

interface ProjectsProps {
  projects: Project[];
}

interface CardData {
  title: string;
  subtitle: string;
  category: string;
  image: string;
  external: string;
}

const featuredCards: CardData[] = [
  {
    title: 'وسم',
    subtitle: 'مشروع شخصي',
    category: 'SaaS',
    image: '/images/projects/wasm.webp',
    external: 'https://usewasm.com/',
  },
  {
    title: 'Day Day Game',
    subtitle: 'مجمع ترفيهي — الرياض',
    category: 'موقع تجاري',
    image: '/images/projects/daydaygame.webp',
    external: 'https://daydaygame.sa/',
  },
  {
    title: 'Picssync',
    subtitle: 'مشروع شخصي',
    category: 'SaaS',
    image: '/images/projects/picssync.webp',
    external: 'https://picssync.ftq.sa',
  },
  {
    title: 'لمّة',
    subtitle: 'مشروع شخصي',
    category: 'منصة ألعاب',
    image: '/images/projects/lamma.webp',
    external: 'https://lamma.ftq.sa',
  },
  {
    title: 'وريث',
    subtitle: 'فريق التراث التطوعي',
    category: 'منصة تراثية',
    image: '/images/projects/wareeth.webp',
    external: 'https://wareeth.org',
  },
];

const Projects: React.FC<ProjectsProps> = ({ projects: _projects }) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft;
    // In RTL, scrollLeft becomes negative or behaves differently. Normalize via absolute value.
    const pos = Math.abs(left);
    setCanScrollPrev(pos > 4);
    setCanScrollNext(pos < max - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = (dir: 'prev' | 'next') => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    const isRtl = document.documentElement.dir === 'rtl';
    const sign = dir === 'next' ? 1 : -1;
    // For RTL, scrolling "next" visually moves content right-to-left, but scrollBy direction matches logical
    el.scrollBy({ left: (isRtl ? -1 : 1) * sign * amount, behavior: 'smooth' });
  };

  return (
    <section className="latest-works" id="projects">
      <div className="latest-works-inner">
        <div className="latest-works-header">
          <div className="latest-works-title-group">
            <div className="latest-works-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3.5 7L7 3.5L10.5 7L7 10.5L3.5 7Z" stroke="white" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="latest-works-title">أحدث أعمالي</h2>
          </div>

          <Link to="/works" className="latest-works-browse">
            <span>تصفح جميع الأعمال</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 3.5L5.5 7L9 10.5" stroke="#0369A1" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

        <div className="latest-works-carousel">
          <button
            type="button"
            className="latest-works-nav latest-works-nav-prev"
            onClick={() => scrollBy('prev')}
            disabled={!canScrollPrev}
            aria-label="السابق"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="#0369A1" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="latest-works-track" ref={trackRef}>
            {featuredCards.map((card) => (
              <a
                key={card.title}
                href={card.external}
                target="_blank"
                rel="noopener noreferrer"
                className="work-card"
              >
                <div
                  className="work-card-image"
                  style={{ backgroundImage: `url(${card.image})` }}
                  role="img"
                  aria-label={card.title}
                />
                <div className="work-card-footer">
                  <div className="work-card-meta">
                    <div className="work-card-icon-bubble" aria-hidden="true">
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M3.5 9.5L9.5 3.5M9.5 3.5H4.5M9.5 3.5V8.5" stroke="#0369A1" strokeWidth="1.08" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="work-card-text">
                      <h3 className="work-card-title">{card.title}</h3>
                      <p className="work-card-subtitle">{card.subtitle}</p>
                    </div>
                  </div>
                  <span className="work-card-category">{card.category}</span>
                </div>
              </a>
            ))}
          </div>

          <button
            type="button"
            className="latest-works-nav latest-works-nav-next"
            onClick={() => scrollBy('next')}
            disabled={!canScrollNext}
            aria-label="التالي"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 4L6 8L10 12" stroke="#0369A1" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <p className="latest-works-private-note">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4" y="10" width="16" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <span>هناك مشاريع أخرى لا يمكن عرضها لكونها تحت اتفاقية سرية مع عملائها.</span>
        </p>
      </div>
    </section>
  );
};

export default Projects;
